/**
 * AI Summary Section - displays LLM-generated clinical summary
 *
 * Placed above Primary Risk Factors Summary in the resident detail dialog.
 * Shows a "temporarily unavailable" message when ai_summary_status is "failed".
 * Hidden when ai_summary is null and status is not "failed" (graceful degradation).
 * Info icon opens a dialog showing structured reasoning for the summary.
 */

import React, { useState } from "react";
import { Info, XCircle } from "lucide-react";
import { SparkleIcon } from "../../atoms/SparkleIcon";
import { AiFeedback } from "../../molecules/AiFeedback";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "../../ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../ui/tooltip";
import type { AiFeedbackSentiment } from "../../../types/residents";

interface AiSummaryReasoningItem {
  factor: string;
  justification: string;
}

interface AiSummarySectionProps {
  summary: string | null;
  isLoading: boolean;
  aiSummaryStatus?: string;
  aiSummaryReasoning?: AiSummaryReasoningItem[] | null;
  /** UI-visibility-only switch — reasoning is still generated/stored when false. */
  aiReasoningVisible?: boolean;
  residentId?: string;
  riskDocumentId?: string;
  wqId?: string;
  insightsDays?: number;
  existingFeedbackSentiment?: AiFeedbackSentiment | null;
  existingFeedbackComment?: string | null;
  additionalSummary?: string | null;
  additionalSummaryStatus?: string;
}

export const AiSummarySection: React.FC<AiSummarySectionProps> = ({
  summary,
  isLoading,
  aiSummaryStatus,
  aiSummaryReasoning,
  aiReasoningVisible = false,
  residentId,
  riskDocumentId,
  wqId,
  insightsDays,
  existingFeedbackSentiment,
  existingFeedbackComment,
  additionalSummary,
  additionalSummaryStatus,
}) => {
  const [reasoningOpen, setReasoningOpen] = useState(false);
  const hasReasoning =
    aiReasoningVisible && aiSummaryReasoning && aiSummaryReasoning.length > 0;
  const daysLabel = insightsDays ? `for the Last ${insightsDays} Days` : "";
  const showAdditional = insightsDays !== undefined && insightsDays !== 30;

  // Loading state: show skeleton
  if (isLoading) {
    return (
      <section>
        <div className="rounded-sm border border-ai bg-ai-background px-4 pb-2">
          <div className="flex items-center gap-2">
            <SparkleIcon className="h-10 w-10 text-ai" />
            <div className="flex-1 pt-2">
              <h3 className="text-base font-medium text-ai-foreground">AI Summary {daysLabel}</h3>
            </div>
          </div>
          <div className="pl-10">
            <div className="animate-pulse space-y-2">
              <div className="h-4 bg-gray-200 rounded w-full" />
              <div className="h-4 bg-gray-200 rounded w-5/6" />
              <div className="h-4 bg-gray-200 rounded w-4/6" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Failed to generate: show brief message instead of hiding
  if (!summary && aiSummaryStatus === "failed") {
    return (
      <section>
        <div className="rounded-sm border border-gray-200 bg-gray-50 px-4 pb-2">
          <div className="flex items-center gap-2">
            <SparkleIcon className="h-10 w-10 text-gray-400" />
            <div className="flex-1 pt-2">
              <h3 className="text-base font-medium text-gray-900">AI Summary {daysLabel}</h3>
            </div>
          </div>
          <div className="pl-10">
            <p className="text-sm text-gray-500 italic">
              Summary temporarily unavailable. Will retry on next calculation.
            </p>
          </div>
        </div>
      </section>
    );
  }

  // No summary available (disabled, skipped, or not generated): hide section
  if (!summary) {
    return null;
  }

  // SUCCESS case: show summary
  return (
    <section>
      <div className="rounded-sm border border-ai bg-ai-background px-4 pb-2">
        <div className="flex items-center gap-2">
          <SparkleIcon className="h-10 w-10 text-ai" />
          <div className="flex-1 pt-2 flex items-center gap-1.5">
            <h3 className="text-base font-medium text-ai-foreground">AI Summary {daysLabel}</h3>
            {hasReasoning && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setReasoningOpen(true)}
                    className="text-ai-foreground/60 hover:text-ai-foreground transition-colors cursor-pointer"
                    aria-label="View AI reasoning"
                  >
                    <Info className="w-4 h-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" sideOffset={4}>
                  View AI reasoning
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          {residentId && riskDocumentId && (
            <AiFeedback
              residentId={residentId}
              contentType="ai_summary"
              riskDocumentId={riskDocumentId}
              wqId={wqId}
              insightsDays={insightsDays}
              existingSentiment={existingFeedbackSentiment}
              existingComment={existingFeedbackComment}
            />
          )}
        </div>
        <div className="pl-10">
          <p className="text-sm font-normal leading-[21px] text-ai-foreground">{summary}</p>
        </div>
      </div>

      {/* Reasoning Dialog */}
      {hasReasoning && (
        <Dialog open={reasoningOpen} onOpenChange={setReasoningOpen}>
          <DialogContent
            className="p-0 gap-0 overflow-hidden bg-card [&>button]:hidden flex flex-col max-h-[80vh]"
            style={{ width: "520px", maxWidth: "calc(100vw - 2rem)" }}
          >
            {/* Teal Header */}
            <div className="px-8 py-5 relative rounded-t bg-primary">
              <button
                onClick={() => setReasoningOpen(false)}
                className="absolute top-5 right-5 text-primary-foreground/80 hover:text-primary-foreground transition-colors"
                aria-label="Close"
              >
                <XCircle className="h-6 w-6" />
              </button>
              <DialogTitle
                className="text-primary-foreground"
                style={{ fontSize: "20px", fontWeight: 700 }}
              >
                AI Summary Reasoning
              </DialogTitle>
              <DialogDescription className="text-base text-primary-foreground/90 mt-1">
                Why each risk factor was highlighted
              </DialogDescription>
            </div>

            {/* Reasoning Items */}
            <div className="px-8 py-5 space-y-4 overflow-y-auto min-h-0 flex-1">
              {aiSummaryReasoning!.map((item, index) => (
                <div key={index}>
                  {index > 0 && <div className="border-t border-gray-200 mb-4" />}
                  <p className="text-sm font-semibold text-gray-900">
                    {item.factor}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    {item.justification}
                  </p>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Additional 30-day insights when viewing a shorter time range */}
      {showAdditional && additionalSummary && (
        <div className="rounded-sm border border-ai/50 bg-ai-background/50 px-4 pb-2 mt-3">
          <div className="flex items-center gap-2">
            <SparkleIcon className="h-10 w-10 text-ai/70" />
            <div className="flex-1 pt-2">
              <h3 className="text-base font-medium text-ai-foreground">
                Additional Insights for the Last 30 Days
              </h3>
            </div>
          </div>
          <div className="pl-10">
            <p className="text-sm font-normal leading-[21px] text-ai-foreground">{additionalSummary}</p>
          </div>
        </div>
      )}
      {showAdditional && !additionalSummary && additionalSummaryStatus === "failed" && (
        <div className="rounded-sm border border-gray-200 bg-gray-50 px-4 pb-2 mt-3">
          <div className="flex items-center gap-2">
            <SparkleIcon className="h-10 w-10 text-gray-400" />
            <div className="flex-1 pt-2">
              <h3 className="text-base font-medium text-gray-900">
                Additional Insights for the Last 30 Days
              </h3>
            </div>
          </div>
          <div className="pl-10">
            <p className="text-sm text-gray-500 italic">
              Summary temporarily unavailable. Will retry on next calculation.
            </p>
          </div>
        </div>
      )}
    </section>
  );
};
